<script setup>
import { DialogContent, useForwardPropsEmits } from "reka-ui";
import { useAttrs } from "vue";

import SvgIcon from "@/components-ng/SvgIcon.vue";

import DialogClose from "./DialogClose.vue";
import DialogOverlay from "./DialogOverlay.vue";
import DialogPortal from "./DialogPortal.vue";

const props = defineProps({
  forceMount: { type: Boolean, default: false },
  trapFocus: { type: Boolean, default: true },
  disableOutsidePointerEvents: { type: Boolean, default: true },
  asChild: { type: Boolean, default: false },
  as: { type: [String, Object], default: "div" },
});

const emit = defineEmits([
  "openAutoFocus",
  "closeAutoFocus",
  "escapeKeyDown",
  "pointerDownOutside",
  "interactOutside",
]);

const forwarded = useForwardPropsEmits(props, emit);
const attrs = useAttrs();

defineOptions({ inheritAttrs: false });
</script>

<template>
  <DialogPortal>
    <DialogOverlay />
    <DialogContent v-bind="{ ...forwarded, ...attrs }" class="dialog-content">
      <slot />
      <DialogClose class="dialog-close" aria-label="Close">
        <SvgIcon name="system/close" width="16" height="16" />
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/palette" as p;

.dialog-content {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 1001;
  width: 100%;
  max-width: 512px;
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  padding: 24px;
  background-color: c.$background-ui;
  border: 1px solid c.$border-subtle;
  border-radius: 12px;
  box-shadow: 0 24px 48px p.$black-alpha-20;
  transform: translate(-50%, -50%);

  &[data-state="open"] {
    animation: dialog-in 0.18s ease-out;
  }

  &[data-state="closed"] {
    animation: dialog-out 0.15s ease-in;
  }

  &:focus {
    outline: none;
  }
}

.dialog-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 28px;
  height: 28px;
  appearance: none;
  border: none;
  border-radius: 6px;
  background: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: c.$text-subtle;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: c.$background-grey-light;
    color: c.$text;
  }
}

@keyframes dialog-in {
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes dialog-out {
  from {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }

  to {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.96);
  }
}
</style>
